import React, { useContext, useEffect, useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import dayjs from "dayjs";
import * as Yup from "yup";

import {
  usePricingAverages,
  usePricing,
  usePriceAnalytics,
} from "../../../product/hooks";
import { useUpsertQuote } from "../../../order/hooks/quotes";
import { useQuoteProductPricing } from "../../../order/hooks/quoteProductPricing";
import FormikAutoSave from "./FormikAutoSave";
import { Button } from "../../../components/ui";
import { moneyFormatter } from "../../../core/utils/formatters";
import { Visible } from "../../../core/components";
import { useAdmins } from "../../../hooks/admins";
import { FieldGroup, Loading } from "../../../components";
import { UserContext } from "../../../contexts";
import {
  boxOptions,
  conditionOptions,
  monthOptions,
  yearOptions,
} from "../../../product/constants";
import {
  currencyOptions,
  getBaseCurrency,
  isMulticurrency,
} from "../../../core/currency";
import { floor } from "lodash";

// Pre-multicurrency wording for the legacy five (EURO/DOLLARS labels).
const LEGACY_CURRENCY_NAMES = { eur: "EURO", usd: "DOLLARS" };
const quoteCurrencyOptions = () =>
  currencyOptions().map((o) => ({
    ...o,
    name: LEGACY_CURRENCY_NAMES[o.id] ?? o.name,
  }));

const preparePricing = (pricing) => {
  if (!Array.isArray(pricing)) {
    return [];
  }

  return pricing.map((item) => ({
    id: item.id,
    name: [
      dayjs(item.price_date).format("DD/MM/YYYY"),
      item.source,
      item.year_month.split(" ")[0],
      item.year_month.split(" ")[1],
      conditionOptions[item.condition],
      moneyFormatter(item.price / 100, item.currency),
      boxOptions[item.delivery_set],
      item.supplier_id ? item.supplier_name : item.supplier,
      item.location?.toUpperCase(),
      ...(item.currency !== "gbp"
        ? [`[${moneyFormatter(item.gbp_price / 100, "gbp")}]`]
        : []),
    ]
      .filter((val) => val)
      .join(" | "),
  }));
};

const prepareListing = async (listing, setListingItems) => {
  if (!Array.isArray(listing)) {
    return [];
  }

  const listingOptions = listing.map((item) => ({
    id: item.id,
    price: item.price,
    name: [
      moneyFormatter(item.price / 100, item.currency),
      item.year,
      item.condition,
      item.box,
      item.buying_price && moneyFormatter(item.buying_price / 100, item.buying_currency),
    ]
      .filter((val) => val)
      .join(" | "),
  }));

  setListingItems(listingOptions);
};

const validationSchema = (isOrder, id) => {
  const schema = Yup.object().shape({
    user_id: Yup.number()
      .typeError("Invalid")
      .required("Required."),
    reviewer_id: Yup.string()
      .optional()
      .nullable()
      .typeError("Invalid"),
    date: Yup.date()
      .typeError("Invalid")
      .required("Required"),
  });

  if (id == "select-listing") {
    schema.shape({
      listing_id: Yup.string().required("Required."),
    });
  } else {
    schema.shape({
      price: Yup.string().required("Required."),
    });
  }

  if (isOrder) {
    schema.shape({
      condition: Yup.string()
        .typeError("Invalid")
        .required("Required."),
      delivery_set: Yup.string().required("Required."),
      year: Yup.number()
        .typeError("Invalid")
        .required("Required."),
    });
  } else {
    schema.shape({
      min_price: Yup.number("Invalid").typeError("Invalid"),
      max_price: Yup.number("Invalid").typeError("Invalid"),
    });
  }

  return schema;
};

// A worked-out value, read from the form: label above, the figure as text.
const StaticFigure = ({ name, slot, label, profitKey, setProfitPrices }) => {
  const { values } = useFormikContext();
  const value = values[name];

  // The figures strip shows what this field shows: fill the strip's median from
  // it when nothing else has (the averages effect only covers some products).
  useEffect(() => {
    if (!value) return;
    setProfitPrices?.((prices) =>
      prices?.[profitKey] ? prices : { ...prices, [profitKey]: Math.round(parseFloat(value) * 100), avgCurrency: "gbp" }
    );
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`qws-field qws-field--${slot} qws-field--static`}>
      <span className="qws-field__label">{label}</span>
      <span className="qws-field__figure">{value ? moneyFormatter(value, "gbp") : "—"}</span>
    </div>
  );
};

const QuoteForm = ({
  id,
  quote,
  item,
  onDone,
  navigateOnSave = true,
  setProfitPrices,
  consignPrices,
  quoteSubmitRef,
  // The quote workspace (new setup): an existing quote saves on change; a new
  // one keeps a single "Create quote" button (creating is a discrete action).
  autoSave = false,
  // "grid": the quote workspace's layout — every field labelled above, same
  // size, in a fixed order (Currency · Price · Quoted by · Quote date, then the
  // pricing fields). Anything else is the modal's layout, unchanged.
  layout = "legacy",
  // Overrides the store flag for the PRODUCT PRICING dropdown and the median
  // inputs (the workspace decides for itself — see QuoteWorkspace).
  productPricing,
}) => {
  const { upsertQuote } = useUpsertQuote();
  const { user } = useContext(UserContext);
  const { admins } = useAdmins({ forOptions: true });
  const isOrder = item?.request_type === "order";
  const isSell = item?.request_type === "sell";
  const grid = layout === "grid";
  // Legacy quote pricing workflow, per store. OFF hides the PRODUCT PRICING
  // dropdown and the TRADE / MARKET MEDIAN inputs. The medians are still
  // seeded and SAVED exactly as before (Kanban Straight/Consign, PriceIQ sell
  // margins and client CLTV read them) — only the inputs are hidden.
  const storeProductPricing = useQuoteProductPricing();
  const hasProductPricing = productPricing ?? storeProductPricing;
  const showProductPricing = isOrder && hasProductPricing;
  // Grid: a labelled field in a named slot. Legacy: the modal's column + its
  // in-field label, exactly as before.
  const col = (legacy, slot) => (grid ? `qws-field qws-field--${slot}` : legacy);
  const textLabel = (legacyText, text, paddingLeft) =>
    grid
      ? { label: text }
      : { placeholder: legacyText, persistLabel: true, inputStyle: { paddingLeft } };
  const selectLabel = (legacyText, text) =>
    grid ? { label: text, placeholder: "Select…" } : { placeholder: legacyText };

  // Always call hooks but conditionally enable them
  const { pricing, isLoading: isLoadingPricing } = usePricing(
    showProductPricing ? item?.product_id : null,
    "trade",
    id
  );

  const { averages, isLoading: isLoadingAverages } = usePricingAverages(
    isSell ? item?.product_id : null
  );
  const { analytics } = usePriceAnalytics(item?.product);
  const { market, trade } = analytics;
  const [error, setError] = useState(null);
  const [listingItems, setListingItems] = useState([]);

  // Derived from the query data itself, not its length. The list is capped at
  // the 50 newest prices, so for a product that already has 50 a price added on
  // the Pricing tab leaves the length unchanged — and a length-keyed rebuild
  // kept showing the cached list until a hard refresh. react-query hands back
  // a new array whenever the refetch brings different data.
  const pricingItems = useMemo(() => preparePricing(pricing), [pricing]);

  useEffect(() => {
    if (item.orderListing?.length) {
      prepareListing(item.orderListing, setListingItems);
    }
  }, [item.orderListing?.length]);

  useEffect(() => {
    // Seed the profit-widget currency (sell currency for an order, buy currency
    // for a sell/buy request) from the record's own currency when present, else
    // the store base currency. Never hardcode a non-gbp default — getBaseCurrency()
    // resolves to 'gbp' until multi-currency is seeded, so this stays byte-identical.
    setProfitPrices((prices) => ({
      ...prices,
      [isOrder ? "sellCurrency" : "buyCurrency"]:
        quote?.currency || getBaseCurrency(),
    }));
  }, []);

  useEffect(() => {
    // Only update profit prices with averages for sell requests
    if (isSell && averages) {
      setProfitPrices((prices) => ({
        ...prices,
        trade: prices?.trade
          ? prices.trade
          : averages?.trade
          ? averages.trade
          : 0,
        market: prices?.market
          ? prices.market
          : averages?.market
          ? averages.market
          : 0,
        // trade/market medians are GBP magnitudes (seeded from *.month.gbp), so
        // their FX base is gbp — QuoteView hardcodes avgCurrency 'gbp' to match.
        avgCurrency: "gbp",
      }));
    }
  }, [isSell, averages?.trade, averages?.market, averages?.currency]);

  const initialValues = useMemo(
    () => ({
      request_id: quote?.request_id || item?.id,
      user_id: quote?.user_id || user?.id,
      currency: quote?.currency || getBaseCurrency(),
      product_price_id: quote?.product_price_id || null,
      // condition is `array|nullable` on a request — a sell quote whose request
      // has no condition set makes `quote.request.condition` null, and `[0]` on
      // null threw, crashing the Details tab (QuoteForm). Optional-index it.
      condition: isOrder ? null : quote?.request?.condition?.[0],
      delivery_set: isOrder ? null : quote?.request?.delivery_set,
      year: isOrder ? null : quote?.request?.year,
      month: isOrder ? null : "00",
      price: quote?.price ? quote.price / 100 : "",
      date: quote?.date ? dayjs(quote.date) : new Date(),
      min_price: quote?.min_price ? quote.min_price / 100 : "",
      max_price: quote?.max_price ? quote.max_price / 100 : "",
      trade_avg: quote?.trade_avg
        ? quote.trade_avg / 100
        : trade?.month?.gbp
        ? trade?.month?.gbp / 100
        : "",
      market_avg: quote?.market_avg
        ? quote.market_avg / 100
        : market?.month?.gbp
        ? market?.month?.gbp / 100
        : "",
    }),
    [item.id, quote, trade, market]
  );

  if (
    (id !== "new" && id !== "select-listing" && !quote) ||
    (showProductPricing && isLoadingPricing) ||
    (isSell && isLoadingAverages)
  ) {
    return <Loading />;
  }
  const isCreating = id === "new" || id === "select-listing";
  const roundedValue = (value) => (value ? floor(value / 50) * 50 : null);

  // TRADE/MARKET MEDIAN are GBP magnitudes end-to-end (seeded from
  // *.month.gbp, persisted as GBP trade_avg/market_avg, and the profit
  // widgets convert them via avgCurrency 'gbp'). They must NOT be converted
  // into the quote currency here — that would corrupt the stored values on
  // save — but they must not READ as the quote currency either. Tag the
  // fields "(GBP)" when multi-currency is on (same pattern as the Market
  // Data CH24 form); byte-identical while off.
  const gbpTag = isMulticurrency() ? " (GBP)" : "";

  const handleSubmit = async (data) => {
    setError(null);

    let price = Math.round(parseFloat(data.price) * 100);

    if (data.listing_id) {
      console.log(data.listing_id, listingItems);
      const listingPrice = listingItems?.find(
        (listing) => listing.id === data.listing_id
      );
      price = listingPrice?.price;
    }

    const quoteData = {
      ...data,
      price,
      date: dayjs(data.date).format("YYYY-MM-DD"),
      product_id: item.product_id,
      trade_avg: data.trade_avg ? Math.round(parseFloat(data.trade_avg) * 100) : null,
      market_avg: data.market_avg ? Math.round(parseFloat(data.market_avg) * 100) : null,
    };

    if (item.request_type === "sell") {
      quoteData.min_price = roundedValue(parseInt(data.min_price)) * 100;
      quoteData.max_price = roundedValue(parseInt(data.max_price)) * 100;
    }

    try {
      const quote = await upsertQuote({ id, quoteData, navigateOnSave });

      onDone(quote);
    } catch (error) {
      console.log(error);
      setError("The quote could not be saved");
    }
  };

  return (
    <div className="quote-form">
      <Formik
        {...{
          initialValues,
          validationSchema: validationSchema(isOrder, id),
          onSubmit: handleSubmit,
        }}
      >
        <Form>
          <div className={grid ? "qws-form-grid" : "row"}>
            <div className={col("col-12 col-sm-6", "admin")}>
              <FieldGroup
                name="user_id"
                  type="select"
                  options={admins}
                  {...selectLabel("ADMIN", "Quoted by")}
                />
              </div>

              {grid ? (
                <div className="qws-field qws-field--date">
                  <label htmlFor="qws-quote-date">Quote date</label>
                  <input
                    id="qws-quote-date"
                    readOnly
                    value={dayjs(quote?.date || undefined).format("DD/MM/YYYY")}
                  />
                </div>
              ) : (
                <div className="col-12 col-sm-6"></div>
              )}

              <div className={col("col-12 col-sm-6", "currency")}>
                <FieldGroup
                  name="currency"
                  type="select"
                  options={quoteCurrencyOptions()}
                  {...selectLabel("CURRENCY", "Currency")}
                  onChange={(value) => {
                    setProfitPrices((prices) => ({
                      ...prices,
                      [isOrder ? "sellCurrency" : "buyCurrency"]: value,
                    }));
                  }}
                />
              </div>

              <div className={col("col-12 col-sm-6", "price")}>
                {id == "select-listing" ? (
                  <FieldGroup
                    name="listing_id"
                    type="select"
                    options={listingItems}
                    {...selectLabel("Select Listing", "Listing")}
                  />
                ) : (
                  <FieldGroup
                    name="price"
                    type="numberFormat"
                    {...textLabel(
                      `${isOrder ? "SELLING" : "BUYING"} PRICE:`,
                      `${isOrder ? "Selling" : "Buying"} price`,
                      "130px"
                    )}
                    onChange={(value) => {
                      if (isNaN(value)) {
                        return;
                      }

                      setProfitPrices((prices) => ({
                        ...prices,
                        [isOrder ? "sell" : "buy"]: Math.round(parseFloat(value) * 100),
                      }));
                    }}
                  />
                )}
              </div>

              <Visible when={showProductPricing}>
                <div className={col("col-12 col-sm-12", "product-pricing")}>
                  <FieldGroup
                    name="product_price_id"
                    type="select"
                    options={pricingItems}
                    {...selectLabel("Product Pricing", "Product pricing · buying from")}
                    onChange={(value) => {
                      const productPrice = pricing.find(
                        (price) => price.id === value
                      );

                      if (!productPrice) {
                        return;
                      }

                      setProfitPrices((prices) => ({
                        ...prices,
                        buy: productPrice?.price,
                        buyCurrency: productPrice?.currency,
                      }));
                    }}
                  />
                </div>
              </Visible>

              <Visible when={!isOrder}>
                <div className={col("col-12 col-sm-6 readonly", "consign-min")}>
                  <FieldGroup
                    name="min_price"
                    type="numberFormat"
                    {...textLabel("CONSIGN MIN:", "Consign min", "130px")}
                    value={
                      roundedValue(consignPrices?.min) ||
                      (quote?.min_price ? quote?.min_price / 100 : "")
                    }
                    readOnly={roundedValue(consignPrices?.min)}
                  />
                </div>

                <div className={col("col-12 col-sm-6 readonly", "consign-max")}>
                  <FieldGroup
                    name="max_price"
                    type="numberFormat"
                    {...textLabel("CONSIGN MAX:", "Consign max", "130px")}
                    value={
                      roundedValue(consignPrices?.max) ||
                      (quote?.max_price ? quote?.max_price / 100 : "")
                    }
                    readOnly={roundedValue(consignPrices?.max)}
                  />
                </div>
                <Visible when={false}>
                  <div className="col-12 col-sm-6">
                    <FieldGroup
                      name="condition"
                      type="select"
                      options={conditionOptions}
                      placeholder="Condition"
                    />
                  </div>

                  <div className="col-12 col-sm-6">
                    <FieldGroup
                      name="delivery_set"
                      type="select"
                      options={boxOptions}
                      placeholder="Box"
                    />
                  </div>

                  <div className="col-12 col-sm-6">
                    <FieldGroup
                      name="year"
                      type="select"
                      options={yearOptions}
                      placeholder="Year"
                    />
                  </div>

                  <div className="col-12 col-sm-6">
                    <FieldGroup
                      name="month"
                      type="select"
                      options={monthOptions}
                      placeholder="Month"
                    />
                  </div>
                </Visible>
                {/* Hidden, not removed: initialValues still seeds trade_avg /
                    market_avg from analytics and handleSubmit still saves them,
                    and the averages effect above keeps the header profit fed. */}
                {/* Workspace: the medians come from market data, not the user —
                    shown as figures, still saved with the quote. */}
                <Visible when={hasProductPricing && grid}>
                  <StaticFigure name="trade_avg" slot="trade-median" label={`Trade median${gbpTag}`} profitKey="trade" setProfitPrices={setProfitPrices} />
                  <StaticFigure name="market_avg" slot="market-median" label={`Market median${gbpTag}`} profitKey="market" setProfitPrices={setProfitPrices} />
                </Visible>
                <Visible when={hasProductPricing && !grid}>
                  <div className={col("col-12 col-sm-6 readonly", "trade-median")}>
                    <FieldGroup
                      name="trade_avg"
                      type="numberFormat"
                      {...textLabel(
                        `TRADE MEDIAN${gbpTag}:`,
                        `Trade median${gbpTag}`,
                        gbpTag ? "185px" : "145px"
                      )}
                      onChange={(value) => {
                        if (isNaN(value)) {
                          return;
                        }
  
                        setProfitPrices((prices) => ({
                          ...prices,
                          trade: Math.round(parseFloat(value) * 100),
                        }));
                      }}
                    />
                  </div>
  
                  <div className={col("col-12 col-sm-6 readonly", "market-median")}>
                    <FieldGroup
                      name="market_avg"
                      type="numberFormat"
                      {...textLabel(
                        `MARKET MEDIAN${gbpTag}:`,
                        `Market median${gbpTag}`,
                        gbpTag ? "197px" : "157px"
                      )}
                      onChange={(value) => {
                        if (isNaN(value)) {
                          return;
                        }
  
                        setProfitPrices((prices) => ({
                          ...prices,
                          market: Math.round(parseFloat(value) * 100),
                        }));
                      }}
                    />
                  </div>
                </Visible>
              </Visible>
            </div>

            <Visible when={error}>
              <div className="error">{error}</div>
            </Visible>

            {autoSave && !isCreating && <FormikAutoSave />}

            <div className={`row${autoSave && !isCreating ? " qws-hidden-submit" : ""}`}>
              <div className="col-12 col-sm-6 p-1"></div>
              <div className="col-12 col-sm-6 p-1">
                {/* Kept mounted (hidden) under auto-save: PricingForm submits the
                    quote through this ref after its own save. */}
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  type="submit"
                  ref={quoteSubmitRef}
                  data-btn="quote-save"
                >
                  {autoSave && isCreating ? "Create quote" : "Save"}
                </Button>
              </div>
            </div>
        </Form>
      </Formik>
    </div>
  );
};

export default QuoteForm;
